/**
 * View-once media types + AES-GCM seal/open.
 *
 * A view-once attachment is encrypted with a per-message ephemeral AES-GCM
 * key that is *destroyed* after a single decrypt. Once destroyed, the
 * recipient (and even the same recipient across page reloads) cannot
 * recover the plaintext. Forwarding becomes impossible because the key
 * itself no longer exists.
 *
 * The hardening boundary is the state machine in ViewOnceStore: every
 * transition persists before returning so a crash during reveal cannot
 * rewind to `unviewed`.
 *
 * Mirrors iOS `ViewOnceMedia.swift` byte-for-byte in crypto layout:
 *   - AES-GCM 256 with a random 96-bit (12-byte) nonce generated by
 *     `crypto.getRandomValues`.
 *   - Combined ciphertext layout: nonce(12) | ciphertext(n) | tag(16).
 *     Web SubtleCrypto `AES-GCM` encrypt returns ciphertext+tag concatenated;
 *     we prepend the nonce manually — matching CryptoKit's
 *     `AES.GCM.SealedBox.combined` layout exactly so interop is byte-clean.
 *   - No AAD on the blob itself (iOS does not use AAD on sealForUpload either).
 *     The manifest metadata binding is enforced by the wire-format envelope
 *     `kaether:e2ee:v1:` which carries the manifest JSON inside an E2EE
 *     message — the key never reaches the server.
 *
 * Wire-format context (read-only here — the envelope layer owns serialisation):
 *   The manifest travels inside an existing `kaether:e2ee:v1:` envelope, tagged
 *   with `"kind": "viewOnceMedia"`.  See `kaether_for_web/docs/view-once-media.md`
 *   for the full JSON shape.
 */

// ── State machine alphabet ────────────────────────────────────────────────────

export type ViewOnceState =
  | "unviewed" // ready to consume; manifest + key present on disk
  | "viewing"  // beginConsume() committed; key returned at least once
  | "viewed"   // finishConsume() — UI dismissed the reveal; manifest gone
  | "wiped"    // manifest + key deleted (force-wipe or sender cancellation)
  | "expired"; // server reported the ciphertext is gone (best effort)

export type ViewOnceDirection = "incoming" | "outgoing";

// ── Error types ───────────────────────────────────────────────────────────────

export class ViewOnceNotFoundError extends Error {
  override readonly name = "ViewOnceNotFoundError";
  constructor(mediaId: string) {
    super(`View-once record not found: ${mediaId}`);
  }
}

export class ViewOnceWrongStateError extends Error {
  override readonly name = "ViewOnceWrongStateError";
  constructor(
    public readonly expected: readonly ViewOnceState[],
    public readonly actual: ViewOnceState,
  ) {
    super(
      `View-once wrong state — expected [${expected.join(", ")}], got: ${actual}`,
    );
  }
}

export class ViewOnceStoreError extends Error {
  override readonly name = "ViewOnceStoreError";
  constructor(detail: string) {
    super(`View-once store error: ${detail}`);
  }
}

export class ViewOnceCryptoError extends Error {
  override readonly name = "ViewOnceCryptoError";
  constructor(detail: string) {
    super(`View-once crypto error: ${detail}`);
  }
}

// ── Manifest ──────────────────────────────────────────────────────────────────

/** Current manifest version. Increment when the wire shape changes. */
export const VIEW_ONCE_MANIFEST_VERSION = 1 as const;

/**
 * What gets sent over the wire alongside the message body. The `key` field is
 * the raw AES-GCM key bytes for the ciphertext blob at `blobUrl`. The server
 * NEVER sees this — it travels inside an E2EE-encrypted message.
 *
 * `key` is a `Uint8Array` (32 bytes) in memory; serialised to/from base64 JSON
 * so the wire shape matches iOS exactly.
 */
export interface ViewOnceManifest {
  readonly version: typeof VIEW_ONCE_MANIFEST_VERSION;
  readonly mediaId: string;   // unique per attachment
  readonly blobUrl: string;   // where the encrypted blob lives
  readonly mimeType: string;
  readonly byteLength: number;
  readonly key: Uint8Array;   // 32-byte AES-GCM key — DESTROYED after consume
  readonly createdAt: string; // ISO-8601 timestamp
}

/**
 * JSON-safe form used for IndexedDB storage and wire serialisation.
 * The `key` field is base64-encoded so it round-trips through JSON without loss.
 */
export interface ViewOnceManifestJSON {
  readonly version: typeof VIEW_ONCE_MANIFEST_VERSION;
  readonly mediaId: string;
  readonly blobUrl: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly key: string; // base64
  readonly createdAt: string;
}

export function manifestToJSON(m: ViewOnceManifest): ViewOnceManifestJSON {
  return { ...m, key: uint8ToBase64(m.key) };
}

export function manifestFromJSON(j: ViewOnceManifestJSON): ViewOnceManifest {
  return { ...j, key: base64ToUint8(j.key) };
}

// ── Record ────────────────────────────────────────────────────────────────────

/**
 * Persistent record. Carries the manifest while unviewed or viewing; sheds it
 * on transition to any terminal state. The store serialises this to IndexedDB
 * using ViewOnceRecordJSON so wiped records contain zero key bytes.
 */
export interface ViewOnceRecord {
  readonly mediaId: string;
  readonly direction: ViewOnceDirection;
  readonly state: ViewOnceState;
  /** Present only while state is `unviewed` or `viewing`. */
  readonly manifest: ViewOnceManifest | null;
  readonly createdAt: string;  // ISO-8601
  readonly lastUpdated: string; // ISO-8601
}

export interface ViewOnceRecordJSON {
  readonly mediaId: string;
  readonly direction: ViewOnceDirection;
  readonly state: ViewOnceState;
  /** Absent (undefined) or null when manifest has been dropped. */
  readonly manifest?: ViewOnceManifestJSON | null;
  readonly createdAt: string;
  readonly lastUpdated: string;
}

export function recordToJSON(r: ViewOnceRecord): ViewOnceRecordJSON {
  return {
    ...r,
    manifest: r.manifest != null ? manifestToJSON(r.manifest) : null,
  };
}

export function recordFromJSON(j: ViewOnceRecordJSON): ViewOnceRecord {
  return {
    ...j,
    manifest: j.manifest != null && j.manifest !== undefined
      ? manifestFromJSON(j.manifest)
      : null,
  };
}

export function transitioning(
  record: ViewOnceRecord,
  newState: ViewOnceState,
  dropManifest: boolean,
  now: string = new Date().toISOString(),
): ViewOnceRecord {
  return {
    ...record,
    state: newState,
    manifest: dropManifest ? null : record.manifest,
    lastUpdated: now,
  };
}

// ── AES-GCM helpers ───────────────────────────────────────────────────────────

/**
 * Generate a fresh 32-byte AES-GCM key.
 */
export function generateKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Sender-side: encrypt plaintext bytes under a one-shot key.
 *
 * Combined ciphertext layout: nonce(12) | ciphertext(n) | tag(16)
 * This matches CryptoKit `AES.GCM.SealedBox.combined` byte-for-byte.
 *
 * Returns the combined ciphertext and the manifest the sender will hand off
 * in the encrypted message body.
 */
export async function sealForUpload(
  plaintext: Uint8Array,
  mediaId: string,
  blobUrl: string,
  mimeType: string,
): Promise<{ ciphertext: Uint8Array; manifest: ViewOnceManifest }> {
  const keyBytes = generateKey();
  const nonce = crypto.getRandomValues(new Uint8Array(12));

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  // SubtleCrypto AES-GCM returns ciphertext|tag (no nonce prefix).
  const ctAndTag = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128 },
      cryptoKey,
      plaintext,
    ),
  );

  // Prepend nonce: nonce(12) | ciphertext(n) | tag(16)
  const combined = new Uint8Array(nonce.length + ctAndTag.length);
  combined.set(nonce, 0);
  combined.set(ctAndTag, nonce.length);

  const manifest: ViewOnceManifest = {
    version: VIEW_ONCE_MANIFEST_VERSION,
    mediaId,
    blobUrl,
    mimeType,
    byteLength: plaintext.length,
    key: keyBytes,
    createdAt: new Date().toISOString(),
  };

  return { ciphertext: combined, manifest };
}

/**
 * Recipient-side: decrypt combined ciphertext under the manifest's key.
 *
 * Expects the combined format: nonce(12) | ciphertext(n) | tag(16).
 * Caller is responsible for calling this exactly once via
 * ViewOnceCoordinator.consume(); this function is pure crypto.
 */
export async function openCiphertext(
  combined: Uint8Array,
  key: Uint8Array,
): Promise<Uint8Array> {
  if (combined.length < 12 + 16) {
    throw new ViewOnceCryptoError(
      `Combined ciphertext too short: ${combined.length} bytes`,
    );
  }

  const nonce = combined.subarray(0, 12);
  const ctAndTag = combined.subarray(12);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128 },
      cryptoKey,
      ctAndTag,
    );
    return new Uint8Array(plaintext);
  } catch {
    // SubtleCrypto throws a generic DOMException on auth failure — rethrow
    // with a domain-specific error so callers can distinguish this from
    // network errors, store errors, etc.
    throw new ViewOnceCryptoError("AES-GCM authentication failed (wrong key or tampered ciphertext)");
  }
}

// ── Base64 utils ──────────────────────────────────────────────────────────────

export function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
